/**
 * EntranceExitLogs Service
 * 입출입 로그 비즈니스 로직 계층
 */
import type { EntranceExitLog } from '../models/EntranceExitLogs';

export class EntranceExitLogsService {
  /**
   * 시간 포맷팅 (UTC → KST 변환)
   */
  formatTime(timestamp: string): string {
    try {
      // DB에 UTC로 저장되어 있으므로 KST(UTC+9)로 변환 필요
      // 형식: "2025-10-26T15:40:00+00:00" → "2025-10-27 00:40:00"

      // Date 객체로 파싱 (자동으로 로컬 타임존으로 변환됨)
      const date = new Date(timestamp);

      // 한국 시간으로 포맷팅
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hour = String(date.getHours()).padStart(2, '0');
      const minute = String(date.getMinutes()).padStart(2, '0');
      const second = String(date.getSeconds()).padStart(2, '0');

      return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    } catch (error) {
      console.error('시간 포맷 오류:', error, timestamp);
      return timestamp;
    }
  }

  /**
   * 동작 배지 색상 클래스 반환
   */
  getActionBadgeClass(action: string): string {
    // 백엔드에서 게이트 direction에 따라 자동으로 action 설정됨
    // direction="entry" → action="entry" (입장, 초록색)
    // direction="exit" → action="exit" (퇴장, 빨강)
    // outing → action="outing" (외출, 파랑)
    if (action === 'entry') {
      return 'bg-green-100 text-green-800';
    }
    if (action === 'outing') {
      return 'bg-blue-100 text-blue-800'; // 🔵 외출은 파란색
    }
    return 'bg-red-100 text-red-800'; // 퇴장은 빨강
  }

  /**
   * 동작 라벨 반환
   */
  getActionLabel(action: string): string {
    // 백엔드에서 게이트 direction에 따라 자동으로 action 설정됨
    if (action === 'entry') {
      return '입장';
    }
    if (action === 'outing') {
      return '외출';
    }
    return '퇴장';
  }

  /**
   * 외출 유형 라벨 반환
   */
  getOutingTypeLabel(outingType: string | null): string {
    if (!outingType) return '';
    const labels: Record<string, string> = {
      meal: '🍽️ 식사',
      rest: '☕ 휴식',
      hospital: '🏥 병원',
      academy: '📚 학원',
      toilet: '🚻 화장실',
      other: '📝 기타',
    };
    return labels[outingType] || outingType;
  }

  /**
   * 인식 방법 배지 색상 클래스 반환
   */
  getMethodBadgeClass(method: string): string {
    const classes: Record<string, string> = {
      face: 'bg-blue-100 text-blue-800',
      manual: 'bg-purple-100 text-purple-800',
      qr: 'bg-green-100 text-green-800',
      phone: 'bg-yellow-100 text-yellow-800',
      code: 'bg-purple-100 text-purple-800',
    };
    return classes[method] || 'bg-gray-100 text-gray-800';
  }

  /**
   * 인식 방법 라벨 반환
   */
  getMethodLabel(method: string): string {
    const labels: Record<string, string> = {
      face: '얼굴인식',
      manual: '강제 입/퇴실',
      qr: 'QR코드',
      phone: '전화번호',
      code: '번호인식',
    };
    return labels[method] || method;
  }

  /**
   * 신뢰도 포맷팅
   */
  formatConfidence(confidence?: number): string {
    return confidence ? `${(confidence * 100).toFixed(1)}%` : '-';
  }

  /**
   * 입출입 차이 계산
   */
  calculateDifference(totalEntries: number, totalExits: number): string {
    const diff = totalEntries - totalExits;
    return `${diff > 0 ? '+' : ''}${diff}명`;
  }

  /**
   * 페이지 범위 계산
   */
  calculatePageRange(currentPage: number, itemsPerPage: number, totalLogs: number): string {
    const start = (currentPage - 1) * itemsPerPage + 1;
    const end = Math.min(currentPage * itemsPerPage, totalLogs);
    return `전체 ${totalLogs.toLocaleString()}건 중 ${start}-${end}건 표시`;
  }

  /**
   * 페이지 번호 배열 생성
   */
  generatePageNumbers(currentPage: number, totalPages: number): number[] {
    const maxPages = Math.min(5, totalPages);
    const startPage = Math.max(1, Math.min(totalPages - 4, currentPage - 2));

    return Array.from({ length: maxPages }, (_, i) => startPage + i);
  }
}

export const entranceExitLogsService = new EntranceExitLogsService();
